import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { ComponentStateService } from '../component-state/component-state.service';
import { Router } from '@angular/router';
import { ModalService } from '../modal/modal.service';
import { Store } from '@ngrx/store';
import { environment } from '../../environment/environment';
import { ComponentDataSourceService } from '../component-data-source/component-data-source.service';

@Injectable({
  providedIn: 'root'
})
export class FunctionRegistryService {
  private functionRegistry: { [key: string]: Function } = {};
  private functionNamesSubject = new BehaviorSubject<string[]>([]);
  functionNames$ = this.functionNamesSubject.asObservable();

  constructor(
    private componentStateService: ComponentStateService,
    private router: Router,
    private injector: Injector
  ) {
    // Register initial functions
    this.registerFunction('sayHello', (text?: string) => {
      if (text) {
        alert('Hello! ' + text);
      } else {
        alert('Hello!');
      }
    });
    
    this.registerFunction('showConfirm', () => {
      return confirm('Are you sure?');
    });

    this.registerFunction('toggleState', (key: string) => {
      this.componentStateService.toggleState(key);
    });

    this.registerFunction('navigateTo', (route: string | any) => {
      // Convert route to string if it's not already and ensure it starts with a slash
      const routeStr = typeof route === 'string' ? route : String(route);
      const path = routeStr.startsWith('/') ? routeStr : `/${routeStr}`;
      this.router.navigate([path]);
      this.modalService.closeAllModals();
    });

    this.registerFunction('add_event_users', () => {
      this.modalService.showModalByName('add_event_users');
    });

    this.registerFunction('add_event_materials', () => {
      this.modalService.showModalByName('add_event_materials');
    });

    this.registerFunction('add_event_documents', () => {
      this.modalService.showModalByName('add_event_documents');
    });

    this.registerFunction('openModal', (modalName: string, data?: any) => {
      this.modalService.showModalByName(modalName, 1, data);
    });

    this.registerFunction('editPathUser', (pathGuid: string, userUuid: string, row?: any) => {
      const data = row
        ? {
            pathGuid,
            userUuid,
            full_name: row.full_name ?? '',
            requirement_started_at: row.requirement_started_at_raw ?? row.requirement_started_at ?? '',
            maintenance_started_at: row.maintenance_started_at_raw ?? row.maintenance_started_at ?? '',
            version_id: row.version_id ?? null
          }
        : { pathGuid, userUuid };
      this.modalService.showEditPathUserModal(data);
    });

    this.registerFunction('openInNewWindow', (url: string) => {
      window.open(url, '_blank', 'noopener,noreferrer');
    });

    this.registerFunction('removeMaintenance', (versionId: string | number, itemId: number) => {
      const http = this.injector.get(HttpClient);
      const dataSourceService = this.injector.get(ComponentDataSourceService);
      const req$ = http.delete(
        `${environment.apiUrl}/paths/versions/${versionId}/maintenance/${itemId}`,
        { withCredentials: true }
      );
      dataSourceService.updateStoreData(req$, 'educational_path_maintenance').subscribe({
        error: (err) => console.error('[FunctionRegistry] removeMaintenance failed:', err)
      });
    });

    this.registerFunction('removeRequirement', (versionId: string | number, itemId: number) => {
      const http = this.injector.get(HttpClient);
      const dataSourceService = this.injector.get(ComponentDataSourceService);
      const req$ = http.delete(
        `${environment.apiUrl}/paths/versions/${versionId}/requirements/${itemId}`,
        { withCredentials: true }
      );
      dataSourceService.updateStoreData(req$, 'educational_path_requirements').subscribe({
        error: (err) => console.error('[FunctionRegistry] removeRequirement failed:', err)
      });
    });

    this.registerFunction('mailtoEventParticipants', () => {
      const store = this.injector.get(Store);
      const router = this.injector.get(Router);
      
      // Get event GUID from URL
      const urlWithoutQuery = (router.url || '').split('?')[0];
      const pathSegments = urlWithoutQuery.split('/');
      const eventGuid = pathSegments.find((segment: string) =>
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
      );
      
      if (!eventGuid) {
        console.error('[FunctionRegistry] No event GUID found in URL');
        alert('Kunne ikke finde event i URL');
        return;
      }
      
      // Get participants from store
      store.select((state: any) => state.data?.['event_participants']).pipe(
        take(1)
      ).subscribe((participantsData: any) => {
        if (!participantsData || !participantsData.data || !participantsData.data.items) {
          console.error('[FunctionRegistry] No participants data found in store');
          alert('Kunne ikke finde deltagere');
          return;
        }
        
        const participants = participantsData.data.items;
        
        // Extract email addresses (filter out invalid emails and duplicates)
        const emails: string[] = [];
        const emailSet = new Set<string>();
        
        participants.forEach((participant: any) => {
          const email = participant.email || participant.user?.user;
          if (email && 
              typeof email === 'string' && 
              email.trim() !== '' && 
              email !== '-' &&
              email.includes('@')) {
            const normalizedEmail = email.trim().toLowerCase();
            if (!emailSet.has(normalizedEmail)) {
              emailSet.add(normalizedEmail);
              emails.push(email.trim());
            }
          }
        });
        
        if (emails.length === 0) {
          alert('Ingen gyldige email-adresser fundet blandt deltagerne');
          return;
        }
        
        // Create mailto link with BCC
        // Most modern email clients support BCC in mailto links
        // If BCC isn't supported, the email client will typically ignore it
        // Users can manually change BCC to TO in their email client if needed
        // Use semicolon-separated emails for better compatibility
        const emailList = emails.join(';');
        const mailtoLink = `mailto:?bcc=${encodeURIComponent(emailList)}`;
        
        // Open the mailto link
        window.location.href = mailtoLink;
        
      });
    });

  }

  registerFunction(identifier: string, func: Function) {
    this.functionRegistry[identifier] = func;
    this.functionNamesSubject.next(Object.keys(this.functionRegistry));
  }

  executeFunction(identifier: string, ...args: any[]) {
    if (this.functionRegistry[identifier]) {
      return this.functionRegistry[identifier](...args);
    } else {
      console.error(`[FunctionRegistry] Function '${identifier}' not found`);
    }
  }

  getFunctionNames(): string[] {
    return Object.keys(this.functionRegistry);
  }

  getCurrentFunctionNames(): string[] {
    return this.functionNamesSubject.getValue();
  }

  private get modalService(): ModalService {
    return this.injector.get(ModalService);
  }
}
